import React from 'react';
import GeneralIcon from '../assets';

/**
 * Modal to display all returns for an order
 * Shows chronological list of return entries with details
 */
export default function ReturnsModal({ isOpen, onClose, order }) {
  if (!isOpen || !order) return null;

  const returns = order.returns || [];
  const hasReturns = returns.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Devoluções</h2>
            <p className="text-sm text-gray-600 mt-1">Pedido {order.code}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <span className="text-xl text-gray-500">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!hasReturns ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <GeneralIcon iconName="caixa" className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600">Nenhuma devolução registrada</p>
            </div>
          ) : (
            <div className="space-y-6">
              {returns.map((returnEntry, index) => (
                <div key={returnEntry.id} className="border border-gray-200 rounded-lg p-5">
                  {/* Return Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Devolução #{returns.length - index}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(returnEntry.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-lg text-gray-900">
                        R$ {returnEntry.amount.toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </div>

                  {/* Return Details */}
                  <div className="space-y-3">
                    {/* Reason */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Motivo</p>
                      <p className="text-sm text-gray-600 mt-1">{returnEntry.reason}</p>
                    </div>

                    {/* Returned Items */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Produtos devolvidos</p>
                      <div className="space-y-2">
                        {returnEntry.items.map((item, idx) => (
                          <div key={idx} className="flex items-start justify-between text-sm">
                            <div className="flex-1">
                              <p className="text-gray-900">{item.name}</p>
                              {item.variant && (
                                <p className="text-gray-600 text-xs mt-0.5">{item.variant}</p>
                              )}
                            </div>
                            <p className="text-gray-600 ml-4">{item.quantity}x</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Receipt */}
                    {returnEntry.receiptUrl && (
                      <div className="pt-3 border-t border-gray-100">
                        <a
                          href={returnEntry.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <GeneralIcon iconName="documento" className="w-4 h-4" />
                          Visualizar comprovante
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fechar
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled
            title="Em breve: página de validação de devoluções"
          >
            Registrar devolução
          </button>
        </div>
      </div>
    </div>
  );
}
